import { convertAmountToAud } from '~/hooks/apis/fx/useGetConvertedCostPricesInAud';

// A package must clear this margin, whichever category it sits in.
export const MIN_MARGIN_PCT = 15;

// Upstream price inputs have no upper bound, so a stray digit can land e.g. 3.4e22 in a $ field.
const MAX_PLAUSIBLE_PRICE = 1_000_000;

// gross = sell - net, pct = gross/sell. Undefined net (rate not loaded) or out-of-range input means no margin, not a margin of zero.
export function calcMargin(sell: number, net: number | undefined): { gross: number; pct: number } | undefined {
  if (
    net === undefined ||
    !Number.isFinite(sell) ||
    !Number.isFinite(net) ||
    Math.abs(sell) > MAX_PLAUSIBLE_PRICE ||
    Math.abs(net) > MAX_PLAUSIBLE_PRICE
  ) {
    return undefined;
  }
  if (sell <= 0) {
    return undefined;
  }
  const gross = sell - net;
  const pct = (gross / sell) * 100;
  return { gross, pct };
}

// Net rates are in the account currency, sell is always AUD. Returns undefined while the rate is still loading.
export function netToAud(
  net: number,
  netCurrency: string,
  audToSourceCurrencyRate: Record<string, number | undefined>,
): number | undefined {
  if (netCurrency === 'AUD') {
    return net;
  }
  const aud = convertAmountToAud(net, netCurrency, audToSourceCurrencyRate);
  // The workbook's ROUNDDOWN(local / FX, 0), at Excel's 15 digits: USD 65 at 0.65 is 100, not 99.
  return aud === undefined ? undefined : Math.trunc(Number(aud.toPrecision(15)));
}

export function isMarginBelowMinimum(sell: number, pct: number | undefined): boolean {
  return pct !== undefined && sell > 0 && pct < MIN_MARGIN_PCT;
}

// baseGross is undefined for base packages themselves, which have no baseline to compare against.
export function isGrossBelowBase(sell: number, gross: number | undefined, baseGross: number | undefined): boolean {
  return gross !== undefined && baseGross !== undefined && sell > 0 && gross < baseGross;
}
